import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { LANGUAGES, i18n } from '@muzly/shared/i18n';

import { loadPendingProfile, restoreProfile } from '@muzly/api-client';
import type { ProfileView } from '@muzly/api-client';

import { BootDismissOutlet } from './BootDismissOutlet';
import { SETUP_ROUTE, setupReturnState } from '../core';
import { fromProfileView } from '../features/profile/fromProfileView';
import {
  isProfileComplete,
  loadProfile,
  purgeLegacyStorage,
  saveProfile,
} from '../features/profile/profile';

interface RequireProfileProps {
  /** 테스트 주입점. 프로덕션은 기본 복원(2초 상한)을 쓴다. */
  restore?: () => Promise<ProfileView | null>;
}

type GateState = 'restoring' | 'ready' | 'setup';

/**
 * 프로필 게이트 — 초기 설정을 마친 사람만 안쪽(홈·진단·이력·결과·더보기)을 본다.
 *
 * 판정은 마운트 때 한 번이다. 이 요소는 `/setup` **밖**의 레이아웃 라우트라, 설정을
 * 끝내고 `/`로 오면 새로 마운트되어 다시 판정한다 — 상태를 구독할 필요가 없다.
 *
 * 옛 저장소(옛 어휘로 쓰인 프로필·드래프트) 폐기도 여기서 한다: 앱이 프로필을 처음 찾는
 * 자리가 여기고, 폐기는 멱등이라 매 마운트 불러도 두 번째부터는 아무것도 안 한다.
 *
 * **부팅마다 서버에 한 번 물어본다.** 프로필의 정본은 서버라 다른 기기에서 고친 값이
 * 따라와야 한다. 다만 그 요청이 화면을 붙드는 것은 로컬이 비었을 때뿐이다:
 *
 * - 로컬이 완성 → 곧바로 열고, 답이 오면 뒤에서 갱신한다. 서버가 못 줘도 열린 채로 둔다
 * - 로컬이 없음 → 상한(2초)까지 기다리고, 실패도 지연도 `/setup` 이다. 오프라인에서 앱이
 *   안 열리는 것이 설문을 한 번 다시 하는 것보다 나쁘다
 *
 * 아직 못 보낸 수정이 큐에 있으면 서버 값으로 덮지 않는다 — 그 편집이 아직 서버에 안 갔으니
 * 서버 답은 그 사람의 최신이 아니다.
 */
export const RequireProfile = ({
  restore = restoreProfile,
}: RequireProfileProps = {}) => {
  const { pathname, search, hash } = useLocation();
  const [state, setState] = useState<GateState>(() => {
    purgeLegacyStorage();
    return isProfileComplete(loadProfile()?.answers ?? {})
      ? 'ready'
      : 'restoring';
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const view = await restore();
      if (cancelled) {
        return;
      }
      const answers = view ? fromProfileView(view) : {};
      const usable = view !== null && isProfileComplete(answers);
      if (usable && loadPendingProfile(localStorage) === null) {
        saveProfile(answers);
        // 앱이 아는 로케일일 때만 — 서버는 `lang` 값 집합을 아직 제한하지 않는다.
        if (LANGUAGES.some((lng) => lng === view.lang)) {
          void i18n.changeLanguage(view.lang);
        }
      }
      // 이미 열려 있으면 닫지 않는다 — 로컬이 정본인 채로 서버만 못 준 경우다.
      setState((prev) => (prev === 'ready' || usable ? 'ready' : 'setup'));
    })();

    return () => {
      cancelled = true;
    };
    // 마운트 때 한 번만 — restore 의 정체성이 바뀌어도 다시 묻지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'restoring') {
    // 아무것도 그리지 않는다 — 그동안은 index.html 의 부팅 대기 화면(캐릭터)이 덮고 있다.
    // 첫 실행은 복원을 상한(2초)까지 기다리므로, 여기서 걷으면 그 사이가 빈 페이지다.
    return null;
  }
  if (state === 'ready') {
    // 열리는 순간 대기 화면을 흐리게 걷는다.
    return <BootDismissOutlet />;
  }
  // 원래 목적지를 싣는다 — 딥링크로 들어온 사람은 설정을 마친 뒤 그리로 간다.
  // 대기 화면은 설정 화면이 그려질 때 걷히므로 `<Navigate>` 의 빈 프레임도 덮는다.
  return (
    <Navigate
      to={SETUP_ROUTE}
      replace
      state={setupReturnState(pathname + search + hash)}
    />
  );
};
